import type { Channel, Me } from '@novachat/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { client } from '../lib/client';
import { errorMessage } from '../lib/errors';
import { useLive } from '../state/live';
import { Avatar, presenceText } from './Avatar';
import { Modal } from './RoomDialogs';

/** Pick someone to message; the DM opens (or is created) on click. */
export function NewDmDialog({ me, onClose }: { me: Me; onClose: () => void }) {
  const queries = useQueryClient();
  const users = useQuery({ queryKey: ['users'], queryFn: () => client.api.users() });
  const presence = useLive((s) => s.presence);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const search = useRef<HTMLInputElement>(null);
  // The dialog's showModal() focuses its close button first; move to the search field after it.
  useEffect(() => {
    const frame = requestAnimationFrame(() => search.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);
  const people = (users.data ?? []).filter((u) => u.id !== me.id && u.name.toLocaleLowerCase('tr').includes(filter.toLocaleLowerCase('tr')));

  async function open(userId: string, close: () => void) {
    try {
      const dm = await client.api.openDm(userId);
      queries.setQueryData<Channel[]>(['channels'], (list = []) => (list.some((c) => c.id === dm.id) ? list : [...list, dm]));
      useLive.getState().select(dm.id);
      close();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  return (
    <Modal title="Yeni mesaj" onClose={onClose}>
      {(close) => (
        <div className="flex flex-col gap-3">
          <label className="flex h-10 items-center gap-2 rounded-md border border-line-strong bg-surface-2 px-2.5 text-fg-3 focus-within:border-brand-400">
            <Search size={15} aria-hidden="true" />
            <input
              ref={search}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Kişi ara"
              aria-label="Kişi ara"
              className="min-w-0 flex-1 border-0 bg-transparent text-base text-fg-1 outline-none sm:text-sm"
            />
          </label>
          <ul className="m-0 flex max-h-72 list-none flex-col overflow-y-auto p-0">
            {users.isPending && <li className="p-2 text-[13px] text-fg-3">Yükleniyor…</li>}
            {!users.isPending && people.length === 0 && (
              <li className="p-2 text-[13px] leading-normal text-fg-3">{filter ? 'Bu adla kimse yok.' : 'Henüz başka kimse yok. Kişiler NovaChat’e ilk girişlerinde burada görünür.'}</li>
            )}
            {people.map((person) => (
              <li key={person.id}>
                <button
                  type="button"
                  onClick={() => void open(person.id, close)}
                  className="flex w-full items-center gap-2.5 rounded-sm border-0 bg-transparent px-2 py-2 text-left text-sm text-fg-1 hover:bg-surface-2"
                >
                  <Avatar id={person.id} name={person.name} size={28} presence={presence[person.id] ?? 'offline'} />
                  <span className="min-w-0 flex-1 truncate">{person.name}</span>
                  <span className="text-xs text-fg-3">{presenceText(presence[person.id] ?? 'offline')}</span>
                </button>
              </li>
            ))}
          </ul>
          {error && (
            <p role="alert" className="m-0 text-[13.5px] text-[#EE9191]">
              {error}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
